import React from 'react';
import {
    AppstoreOutlined,
    BarChartOutlined,
    BellOutlined,
    BookOutlined,
    ChromeOutlined,
    CloudUploadOutlined,
    CommentOutlined,
    DashboardOutlined,
    DollarOutlined,
    ExperimentOutlined,
    FileSearchOutlined,
    FlagOutlined,
    GiftOutlined,
    GlobalOutlined,
    LayoutOutlined,
    LineChartOutlined,
    NotificationOutlined,
    PartitionOutlined,
    QuestionCircleOutlined,
    SettingOutlined,
    ShopOutlined,
    SkinOutlined,
    SmileOutlined,
    TagsOutlined,
    TeamOutlined,
    TrophyOutlined,
    UserOutlined,
    VideoCameraOutlined,
    WalletOutlined,
    WarningOutlined,
} from '@ant-design/icons';

/**
 * The back-office menu. Every entry has an `icon` (shown in the menu and on the
 * dashboard's quick-access cards). A group holds pages; a page's `scope` is what a user
 * needs to see it (none: everyone with back-office access), and `ownership`
 * 'all' means the `.mine` form of the scope is not enough. A group is shown
 * when at least one of its pages is.
 */
export const NAV_GROUPS = [
    { key: '/dashboard', label: 'Dashboard', icon: <DashboardOutlined /> },
    {
        key: 'catalog-management',
        label: 'Catalog Management',
        icon: <AppstoreOutlined />,
        children: [
            { key: '/ingestion', label: 'Upload Pipeline', scope: 'upload-pipeline.read', bold: true, icon: <CloudUploadOutlined /> },
            { key: '/extension', label: 'Chrome Extension', scope: 'upload-pipeline.write', icon: <ChromeOutlined /> },
            { key: '/catalog', label: 'Catalog', scope: '*', icon: <BookOutlined /> },
            { key: '/category', label: 'Categories', scope: '*', icon: <TagsOutlined /> },
            { key: '/retailer', label: 'Retailers', scope: '*', icon: <ShopOutlined /> },
            { key: '/feedback', label: 'Users Feedback', scope: '*', icon: <CommentOutlined /> },
            { key: '/models-inspector', label: 'Models Inspector', scope: '*', icon: <FileSearchOutlined /> },
        ],
    },
    {
        key: 'challenges',
        label: 'Challenges',
        icon: <TrophyOutlined />,
        children: [
            { key: '/challenges', label: 'Challenges', scope: '*', icon: <FlagOutlined /> },
            { key: '/templates', label: 'Templates', scope: '*', icon: <LayoutOutlined /> },
        ],
    },
    {
        key: 'geo-seo',
        label: 'GEO / SEO',
        icon: <GlobalOutlined />,
        children: [
            { key: '/geo-exposure/queries', label: 'Queries', scope: 'geo-exposure.manage', icon: <QuestionCircleOutlined /> },
            { key: '/geo-exposure', label: 'Answers', scope: 'geo-exposure.manage', icon: <CommentOutlined /> },
            { key: '/geo-exposure/settings', label: 'Configuration', scope: 'geo-exposure.manage', icon: <SettingOutlined /> },
        ],
    },
    {
        key: 'community',
        label: 'Community Management',
        icon: <TeamOutlined />,
        children: [
            { key: '/campaigns', label: 'In-app Campaigns', scope: 'campaigns.manage', icon: <NotificationOutlined /> },
            { key: '/grant-credits', label: 'Grant Gems & Coins', scope: '*', icon: <GiftOutlined /> },
            { key: '/notifications', label: 'Notifications', scope: '*', icon: <BellOutlined /> },
            { key: '/content-reports', label: 'Content Reports', scope: '*', icon: <WarningOutlined /> },
            { key: '/avatar-items', label: 'Avatar Items', scope: '*', icon: <SmileOutlined /> },
        ],
    },
    {
        key: 'management',
        label: 'Management',
        icon: <BarChartOutlined />,
        children: [
            { key: '/report', label: 'Catalog Report', scope: 'upload-pipeline.read', ownership: 'all', icon: <PartitionOutlined /> },
            { key: '/ingestion/costs', label: 'Pipeline Costs', scope: 'upload-pipeline.read', icon: <DollarOutlined /> },
            { key: '/render-performance', label: 'Rendering Performance', scope: '*', icon: <LineChartOutlined /> },
            { key: '/experiments', label: 'Rendering Experiments', scope: '*', icon: <ExperimentOutlined /> },
            { key: '/economy', label: 'Economy', scope: '*', icon: <WalletOutlined /> },
            { key: '/users', label: 'Users', scope: 'admin-users.manage', icon: <UserOutlined /> },
        ],
    },
];

export const CONTENT_REPORTS_KEY = '/content-reports';

/** Every page (a menu entry with a path), in menu order. */
const NAV_PAGES = NAV_GROUPS.flatMap((entry) => entry.children ?? [entry]);

const canSee = (currentUserStore, page) => !page.scope || currentUserStore.can(page.scope, page.ownership);

/** The menu as this user sees it: groups without a visible page are dropped. */
export const visibleNavGroups = (currentUserStore) =>
    NAV_GROUPS.map((entry) => (entry.children ? { ...entry, children: entry.children.filter((page) => canSee(currentUserStore, page)) } : entry))
        .filter((entry) => !entry.children || entry.children.length > 0)
        .filter((entry) => entry.children || canSee(currentUserStore, entry));

export const visiblePages = (currentUserStore) => NAV_PAGES.filter((page) => canSee(currentUserStore, page));

/** The page a path belongs to: the longest menu key the path starts with (`/ingestion/costs` wins over `/ingestion`). */
export const pageForPath = (pathname) =>
    NAV_PAGES.filter((page) => pathname === page.key || pathname.startsWith(`${page.key}/`)).sort((a, b) => b.key.length - a.key.length)[0] ?? null;

export const groupOfPage = (pageKey) => NAV_GROUPS.find((entry) => entry.children?.some((page) => page.key === pageKey))?.key ?? null;
